// The one write core every Director write hook builds on: a request id that is REUSED when the
// identical input is retried (so a lost response never applies a write twice) and renewed when the
// input changes, one request per key while it is in flight (a double click is one write), and
// server errors mapped to the four outcomes a screen can explain.
import { useCallback, useRef, useState } from 'react';

import { requestIdFor, type RetryAttempt } from './request-id';
import { errorMessage, errorStatus } from './use-loader';

export type WriteFailure = 'conflict' | 'forbidden' | 'validation' | 'failed';

export interface WriteError {
  ok: false;
  kind: WriteFailure;
  message: string;
}

export function writeFailure(error: unknown): WriteError {
  const status = errorStatus(error);
  const kind: WriteFailure =
    status === 409
      ? 'conflict'
      : status === 403 || status === 404
        ? 'forbidden'
        : status === 400 || status === 422
          ? 'validation'
          : 'failed';
  return { ok: false, kind, message: errorMessage(error) };
}

/** `write(key, body, send)`: `body` is everything that identifies the write except the request id
 *  (the version included), `send` performs it with the id and resolves what that write returns
 *  (one key always names one kind of write). `pending` holds the keys in flight. `renew(key)`
 *  forgets the key's request id, so its next write mints a new one even for an identical input. */
export function useWrites<Success extends { ok: true }>() {
  const attempts = useRef(new Map<string, RetryAttempt>());
  const inflight = useRef(new Map<string, Promise<Success | WriteError>>());
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());

  const write = useCallback(
    <S extends Success>(
      key: string,
      body: Record<string, unknown>,
      send: (requestId: string) => Promise<S>
    ): Promise<S | WriteError> => {
      const running = inflight.current.get(key) as
        | Promise<S | WriteError>
        | undefined;
      if (running) return running;
      const attempt = requestIdFor(body, attempts.current.get(key) ?? null);
      attempts.current.set(key, attempt);
      setPending(prev => new Set(prev).add(key));
      const promise = send(attempt.requestId)
        .then(
          (result): S | WriteError => {
            // The id is spent: the next write with this key is a new write.
            attempts.current.delete(key);
            return result;
          },
          (error: unknown) => writeFailure(error)
        )
        .finally(() => {
          inflight.current.delete(key);
          setPending(prev => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        });
      inflight.current.set(key, promise);
      return promise;
    },
    []
  );

  const renew = useCallback((key: string) => {
    attempts.current.delete(key);
  }, []);

  return { write, renew, pending };
}
